import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Square } from "lucide-react";
import { SONIOX_VOICES } from "../lib/schemaModes";
import { createChairEngine } from "../lib/chairAudioEngine";
import { postChair, postRescript } from "../api";
import { useToast } from "../../../components/Toast";

interface NightmareRescriptToolProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
}

/* Imagery Rehearsal Therapy: the recurring nightmare rewritten, then the new
   version rehearsed — both scenes rendered symbolically, both narrated; the
   rescripted version loops for rehearsal. */
export function NightmareRescriptTool({ patientId, sessionId, language, getToken }: NightmareRescriptToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [nightmare, setNightmare] = useState("");
  const [rescript, setRescript] = useState("");
  const [voice, setVoice] = useState("Maya");
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState<{ before: string; after: string } | null>(null);
  const [generated, setGenerated] = useState(false);

  // Separate engines so the nightmare plays once while the rescript can loop.
  const nightmareRef = useRef<ReturnType<typeof createChairEngine> | null>(null);
  const rescriptRef = useRef<ReturnType<typeof createChairEngine> | null>(null);

  const onGenerate = async () => {
    try {
      setBusy(true);
      await nightmareRef.current?.close();
      await rescriptRef.current?.close();
      const [visual, chair] = await Promise.all([
        postRescript(getToken, {
          patient_id: patientId, session_id: sessionId,
          scene_prompt: nightmare, rescript_prompt: rescript,
        }),
        postChair(getToken, {
          patient_id: patientId, session_id: sessionId, language,
          chairs: [
            { id: crypto.randomUUID(), mode: "nightmare_rescript", label: "nightmare", text: nightmare, voice },
            { id: crypto.randomUUID(), mode: "nightmare_rescript", label: "rescript", text: rescript, voice },
          ],
        }),
      ]);
      const nightmareEngine = createChairEngine([chair.clips[0]]);
      const rescriptEngine = createChairEngine([chair.clips[1]]);
      const fetchAudio = (url: string) => fetch(url).then((r) => r.arrayBuffer());
      await nightmareEngine.load(fetchAudio);
      await rescriptEngine.load(fetchAudio);
      rescriptEngine.setLoop(true); // rehearsal: the new version repeats
      nightmareRef.current = nightmareEngine;
      rescriptRef.current = rescriptEngine;
      setImages({ before: visual.before_url, after: visual.after_url });
      setGenerated(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg.includes("image_policy_declined") ? t("experiential.policyDeclined") : `${t("experiential.genError")}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const stopAll = () => {
    nightmareRef.current?.stop();
    rescriptRef.current?.stop();
  };

  useEffect(() => {
    return () => {
      nightmareRef.current?.close();
      rescriptRef.current?.close();
    };
  }, []);

  return (
    <div>
      <TextField
        label={t("experiential.nightmareScene")}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={nightmare}
        onChange={(e) => setNightmare(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label={t("experiential.nightmareRescript")}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={rescript}
        onChange={(e) => setRescript(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControl size="small" sx={{ minWidth: 130, mb: 2 }}>
        <InputLabel>{t("experiential.readBy")}</InputLabel>
        <Select value={voice} label={t("experiential.readBy")} onChange={(e) => setVoice(e.target.value)}>
          {SONIOX_VOICES.map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="exp-chair-controls">
        <Button variant="contained" size="small" onClick={onGenerate} disabled={busy || !nightmare.trim() || !rescript.trim()}>
          {t("experiential.generate")}
        </Button>
        {generated && (
          <>
            <Button size="small" variant="outlined" onClick={() => { stopAll(); nightmareRef.current?.play(); }}>
              {t("experiential.playNightmare")}
            </Button>
            <Button size="small" variant="outlined" onClick={() => { stopAll(); rescriptRef.current?.play(); }}>
              {t("experiential.rehearseRescript")}
            </Button>
            <button
              type="button"
              onClick={stopAll}
              aria-label={t("experiential.stop")}
              title={t("experiential.stop")}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "inherit" }}
            >
              <Square size={18} />
            </button>
          </>
        )}
      </div>

      {images && (
        <div className="exp-rescript-grid" style={{ marginTop: 12 }}>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13 }}>{t("experiential.nightmareLabel")}</div>
            <img src={images.before} alt={t("experiential.nightmareLabel")} style={{ width: "100%", borderRadius: 6 }} />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13 }}>{t("experiential.rescriptLabel")}</div>
            <img src={images.after} alt={t("experiential.rescriptLabel")} style={{ width: "100%", borderRadius: 6 }} />
          </div>
        </div>
      )}
    </div>
  );
}
